import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import { Checkbox } from '../atoms';
import type { AdminUserDetail, UserUpdateRequest, UserGender } from '../../types/api/admin-user';
import { USER_GENDERS } from '../../types/api/admin-user';

export interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { payload: UserUpdateRequest; profileImage: File | null }) => void | Promise<void>;
  user?: AdminUserDetail;
}

type UserFormState = {
  name: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: UserGender;
  isActive: boolean;
};

const createDefaultState = (): UserFormState => ({
  name: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  gender: 'PREFER_NOT_TO_SAY',
  isActive: true,
});

export const UserEditModal: React.FC<UserEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
}) => {
  const [formData, setFormData] = useState<UserFormState>(createDefaultState());
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (user && isOpen) {
      console.log('UserEditModal: Pre-filling form with user data:', user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        dateOfBirth: user.dateOfBirth || '',
        gender: user.gender || 'PREFER_NOT_TO_SAY',
        isActive: user.isActive ?? true,
      });
      setImagePreview(user.profileImageUrl || null);
      setProfileImage(null);
      setIsDataLoaded(true);
    } else if (isOpen) {
      console.log('UserEditModal: No user data, using default state');
      setFormData(createDefaultState());
      setImagePreview(null);
      setProfileImage(null);
      setIsDataLoaded(false);
    }
    setErrors({});
  }, [user, isOpen]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, profileImage: 'Please select a valid image file' }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, profileImage: 'Image size must be less than 5MB' }));
        return;
      }
      
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear error
      if (errors.profileImage) {
        setErrors((prev) => { const n = { ...prev }; delete n.profileImage; return n; });
      }
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setImagePreview(user?.profileImageUrl || null);
    if (errors.profileImage) {
      setErrors((prev) => { const n = { ...prev }; delete n.profileImage; return n; });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      // Create payload - tier is excluded, managed via subscription assignment
      const payload: UserUpdateRequest = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        isActive: formData.isActive,
      };
      
      await onSubmit({ payload, profileImage });
      onClose();
    } catch (error) {
      console.error('Failed to update user:', error);
      setErrors({ submit: 'Failed to update user. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit User"
      size="lg"
    >
      {!isDataLoaded && isOpen ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EAB308] mx-auto"></div>
            <p className="text-sm text-gray-600 mt-4">Loading user data...</p>
          </div>
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Image
          </label>
          <div className="flex items-center gap-4">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Profile preview"
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#EAB308] to-[#CA8A04] flex items-center justify-center text-white text-2xl font-semibold">
                {formData.name.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="profile-image-upload"
              />
              <div className="flex gap-2">
                <label
                  htmlFor="profile-image-upload"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 transition-colors cursor-pointer"
                >
                  Choose Image
                </label>
                {profileImage && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, GIF up to 5MB
              </p>
              {errors.profileImage && (
                <p className="text-xs text-red-600 mt-1">{errors.profileImage}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            required
            placeholder="Enter user name"
          />

          <FormField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={errors.email}
            required
            placeholder="Enter email address"
          />

          <FormField
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            error={errors.phone}
            required
            placeholder="Enter phone number"
          />

          <FormField
            label="Date of Birth"
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            error={errors.dateOfBirth}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value as UserGender)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              {USER_GENDERS.filter(g => g.value !== '').map((gender) => (
                <option key={gender.value} value={gender.value}>
                  {gender.label}
                </option>
              ))}
            </select>
          </div>

          {/* User Tier - Read-only display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Tier
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                {user?.tier === 'FREE' && <span className="text-gray-800">🆓 Free</span>}
                {user?.tier === 'PREMIUM' && <span className="text-yellow-800">⭐ Premium</span>}
                {user?.tier === 'VIP' && <span className="text-purple-800">👑 VIP</span>}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              User tier is managed through subscription assignments
            </p>
          </div>
        </div>

        {/* Active Status Checkbox */}
        <div className="flex items-center">
          <Checkbox
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
          <label className="ml-2 text-sm text-gray-700">
            User account is active
          </label>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
      )}
    </Modal>
  );
};

